import {
  PositionFill18Icon,
  PositionOutline18Icon,
} from "@vygruppen/spor-icon-react";
import { isFuture } from "date-fns";
import { phoneNumberWithoutPrefixes } from "features/Header/PuzzelTelephone/utils";
import ElementWrapper from "features/VehicleSidebar/VehicleList/common/ElementWrapper";
import { FC } from "react";
import { generatePath, useSearchParams } from "react-router-dom";
import { CallButton } from "shared/components/buttons/CallButton";
import { TimeInterval } from "shared/components/dataDisplay/TimeInterval";
import {
  Badge,
  HStack,
  SporTooltip,
  Stack,
  Text,
  TooltipContent,
  TooltipTrigger,
  VStack,
} from "shared/components/Spor";
import { ReserveAlternativeTransport } from "shared/types/alternativeTransport";
import {
  getPrintableFromVehicleType,
  isBackupFromPlan,
} from "shared/utils/alternativeTransport";
import { BACKUPBUS } from "shared/utils/routes";
import { useTheme } from "styled-components";

type Props = {
  reserveAlternativeTransport: ReserveAlternativeTransport;
  elementSelected: boolean;
  index: number;
};

/**
 * Beredskapsbuss-specific list element.
 *
 * NB! Temporary duplicate of ReserveOrBackupAlternativeTransportElement so that
 * beredskapsbuss work does not bleed into the reservebuss element.
 */
export const BackupAlternativeTransportElement: FC<Props> = ({
  reserveAlternativeTransport,
  elementSelected,
  index,
}) => {
  const theme = useTheme();
  const [searchParams] = useSearchParams();

  const {
    shiftStartTime,
    shiftEndTime,
    driverPhoneNumber,
    isAvailable,
    baseStopName,
    type,
    hasTracking,
    workShiftId,
  } = reserveAlternativeTransport;

  const hasPhoneNumber = !!driverPhoneNumber && driverPhoneNumber.length > 0;

  const isFutureShift = shiftEndTime && isFuture(shiftEndTime);

  const isPast = !isFutureShift;

  const textColorMain = isPast
    ? theme.colorTextTertiary
    : theme.colorTextSecondary;

  /*
    This is a workaround. If we send in the plus to Puzzel, it is stripped away
    before the 18740 prefix is added. This works fine if you're an admin, but
    not for normal DROPS users. But if we replace the + with 00, it is not stripped,
    and with the 00 it works just fine. Don't ask me why.
   
    These formats work for everyone:
    18740 0047 12345678 <- Result from what we send in with the workaround
    18740 12345678
    0047 12345678
    12345678

    This format only works for admins:
    18740 47 12345678 <- Result from what we send in without the workaround
  */
  const phoneNumberWithDigitsOnly = driverPhoneNumber
    ? driverPhoneNumber.replace("+", "00")
    : null;
  const phoneNumberWithoutPrefix = driverPhoneNumber
    ? phoneNumberWithoutPrefixes(driverPhoneNumber)
    : undefined;

  const providerTitle = isBackupFromPlan(reserveAlternativeTransport)
    ? "Hastus"
    : "Drops";
  const vehicleTitle = `Beredskap ${index} ${workShiftId}`;

  return (
    <ElementWrapper
      selected={elementSelected}
      backgroundColor={theme.colorBackgroundSecondary}
      color={textColorMain}
      isHover
      to={`${generatePath(BACKUPBUS, {
        vehicleUuid: reserveAlternativeTransport.id,
      })}?${searchParams.toString()}`}
      linkState={{ vehicleTitle }}
    >
      <Stack
        flex={1}
        _focusVisible={{
          borderRadius: "6px",
        }}
      >
        <HStack
          fontSize="xs"
          gap="0.25rem"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <VStack alignItems="flex-start" gap="0">
            <HStack>
              <Text fontWeight="bold" color={textColorMain} variant="xs">
                {vehicleTitle}
              </Text>
              <Text color={textColorMain} variant="xs">
                {providerTitle}
              </Text>
            </HStack>

            <Text variant="xs">{`${baseStopName} (${getPrintableFromVehicleType(type, "sentence") ?? ""})`}</Text>
            <TimeInterval
              startTime={shiftStartTime}
              endTime={shiftEndTime}
              disabled={isPast}
              variant="xs"
            />
          </VStack>

          <VStack
            alignItems="flex-end"
            justifyContent="space-between"
            alignSelf="stretch"
          >
            <HStack>
              {!isAvailable && <Badge colorPalette="blue">I bruk</Badge>}
              <SporTooltip>
                {hasTracking ? (
                  <>
                    <TooltipTrigger>
                      <PositionFill18Icon color={theme.colorSuccess} />
                    </TooltipTrigger>
                    <TooltipContent>
                      <Text variant="xs">Denne bussen har aktiv sporing</Text>
                    </TooltipContent>
                  </>
                ) : (
                  <>
                    <TooltipTrigger>
                      <PositionOutline18Icon color={theme.colorSuccess} />
                    </TooltipTrigger>
                    <TooltipContent>
                      <Text variant="xs">
                        Denne bussen har ikke aktiv sporing
                      </Text>
                    </TooltipContent>
                  </>
                )}
              </SporTooltip>
            </HStack>

            {hasPhoneNumber && (
              <CallButton
                phoneNumber={phoneNumberWithDigitsOnly!}
                label={phoneNumberWithoutPrefix}
                size="xs"
              />
            )}
          </VStack>
        </HStack>
      </Stack>
    </ElementWrapper>
  );
};
